import React, { useEffect, useState } from "react";
import { Form, Button, Container } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { AiFillCloseCircle } from "react-icons/ai";
import './CarModelManager.css'

const CarModelManager = () => {
  const [cars, setCars] = useState([]);
  const [makers, setMakers] = useState([]);
  const [models, setModels] = useState([]);

  const [selectedCar, setSelectedCar] = useState("");
  const [selectedMaker, setSelectedMaker] = useState("");
  const [selectedModel, setSelectedModel] = useState("");

  const [carDetails, setCarDetails] = useState("");
  const [makerDetails, setMakerDetails] = useState("");
  const [modelDetails, setModelDetails] = useState("");
  const [carType, setCarType] = useState("");
  const [engineCapacity, setEngineCapacity] = useState("");
  const [serviceType, setServiceType] = useState("");

  const [productCode, setProductCode] = useState("");
  const [headTitle, setHeadTitle] = useState("");
  const [productDesc, setProductDesc] = useState("");
  const [prices, setPrices] = useState([{ plan_name: "", price: "", plan_duration: "" }]);
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await axios.get(
          "https://motor-easy.clickbooster.io/api/cars"
        );
        setCars(response.data);
      } catch (error) {
        console.error("Error fetching car data:", error);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
    const fetchMakers = async () => {
      if (!selectedCar) return;
      try {
        const response = await axios.get(
          `https://motor-easy.clickbooster.io/api/maker?car_id=${selectedCar}`
        );
        setMakers(response.data.makers);
      } catch (error) {
        console.error("Error fetching makers data:", error);
      }
    };
    fetchMakers();
  }, [selectedCar]);

  useEffect(() => {
    const fetchModels = async () => {
      if (!selectedCar || !selectedMaker) return;
      try {
        const response = await axios.get(
          `https://motor-easy.clickbooster.io/api/models?car_id=${selectedCar}&maker_id=${selectedMaker}`
        );
        setModels(response.data);
      } catch (error) {
        console.error("Error fetching models:", error);
      }
    };
    fetchModels();
  }, [selectedCar, selectedMaker]);

  const handlePriceChange = (index, field, value) => {
    const updatedPrices = [...prices];
    updatedPrices[index][field] = value;
    setPrices(updatedPrices);
  };

  const handleRemovePrice = (index) => {
    const updatedPrices = prices.filter((_, i) => i !== index);
    setPrices(updatedPrices);
  };

  const handleAddPrice = () => {
    setPrices([...prices, { plan_name: "", price: "", plan_duration: "" }]);
  };

  const handleAddProduct = async () => {
    try {
      setLoading(true);


      const formData = new FormData();
      formData.append("car_id", carDetails.id);
      formData.append("maker_id", makerDetails.id);
      formData.append("model_id", modelDetails.id);
      formData.append("car_name", carDetails.car_name);
      formData.append("maker_name", makerDetails.maker_name);
      formData.append("model_name", modelDetails.model_name);
      formData.append("car_type", carType);
      formData.append("engine_capacity", engineCapacity);
      formData.append("service_type", serviceType);
      formData.append("product_code", productCode);
      formData.append("head_title", headTitle);
      formData.append("product_desc", productDesc);
      formData.append("prices", JSON.stringify(prices));

      images.forEach((image, index) => formData.append(`images[${index}]`, image));
      videos.forEach((video, index) => formData.append(`videos[${index}]`, video));

      await axios.post("https://motor-easy.clickbooster.io/api/products", formData);

      toast.success("Product added successfully!");
    } catch (error) {
      toast.error("Error adding product.");
    } finally {
      setLoading(false);
    }
  };


  const handleImageChange = (e) => {
    setImages([...images, ...Array.from(e.target.files)]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleVideoChange = (e) => {
    const newVideos = Array.from(e.target.files).map((file) => ({
      file,
      url: URL.createObjectURL(file), // Create object URL here
    }));

    setVideos((prevVideos) => [...prevVideos, ...newVideos]);
  };

  const removeVideo = (index) => {
    // Revoke the object URL to free memory
    URL.revokeObjectURL(videos[index].url);

    setVideos((prevVideos) => prevVideos.filter((_, i) => i !== index));
  };

  return (
    <Container className="mt-5 p-0">
      <h1>Manage Car Models</h1>
      <Form>
        {/* Car Selection */}
        <Form.Group controlId="carSelect">
          <Form.Label>Select Car</Form.Label>
          <Form.Control
            as="select"
            value={selectedCar}
            onChange={(e) => {
              setSelectedCar(e.target.value);
              const car = cars.find((c) => c.id === parseInt(e.target.value));
              setCarDetails(car ? car : "");
            }}
          >
            <option value="">-- Select a Car --</option>
            {cars.map((car) => (
              <option key={car.id} value={car.id}>
                {car.car_name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Maker Selection */}
        <Form.Group controlId="makerSelect">
          <Form.Label>Select Maker</Form.Label>
          <Form.Control
            as="select"
            value={selectedMaker}
            onChange={(e) => {
              setSelectedMaker(e.target.value);
              const maker = makers.find((m) => m.id === parseInt(e.target.value));
              setMakerDetails(maker ? maker : "");
            }}
          >
            <option value="">Select Maker</option>
            {makers.map((maker) => (
              <option key={maker.id} value={maker.id}>
                {maker.maker_name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Model Selection */}
        <Form.Group controlId="modelSelect">
          <Form.Label>Select Model</Form.Label>
          <Form.Control
            as="select"
            value={selectedModel}
            onChange={(e) => {
              setSelectedModel(e.target.value);
              const model = models.find((m) => m.id === parseInt(e.target.value));
              setModelDetails(model ? model : "");
            }}
          >
            <option value="">Select Model</option>
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.model_name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Product Code */}
        <Form.Group controlId="productCodeInput">
          <Form.Label>Product Code</Form.Label>
          <Form.Control
            type="text"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            placeholder="Enter product code"
          />
        </Form.Group>

        {/* Head Title */}
        <Form.Group controlId="headTitleInput">
          <Form.Label>Head Title</Form.Label>
          <Form.Control
            type="text"
            value={headTitle}
            onChange={(e) => setHeadTitle(e.target.value)}
            placeholder="Enter head title"
          />
        </Form.Group>

        {/* Product Description */}
        <Form.Group controlId="productDescInput">
          <Form.Label>Product Description</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={productDesc}
            onChange={(e) => setProductDesc(e.target.value)}
            placeholder="Enter product description"
          />
        </Form.Group>

        {/* Prices */}
        <Form.Group controlId="carType">
          <Form.Label>Car Type</Form.Label>
          <Form.Control type="text" value={carType} onChange={(e) => setCarType(e.target.value)} placeholder="Enter car type" />
        </Form.Group>

        {/* Engine Capacity */}
        <Form.Group controlId="engineCapacity">
          <Form.Label>Engine Capacity</Form.Label>
          <Form.Control type="text" value={engineCapacity} onChange={(e) => setEngineCapacity(e.target.value)} placeholder="Enter engine capacity" />
        </Form.Group>

        {/* Service Type */}
        <Form.Group controlId="serviceType">
          <Form.Label>Service Type</Form.Label>
          <Form.Control type="text" value={serviceType} onChange={(e) => setServiceType(e.target.value)} placeholder="Enter service type" />
        </Form.Group>

        {/* Prices */}
        <Form.Group controlId="pricesInput">
          <Form.Label>Price Plan</Form.Label>
          {prices.map((pricePlan, index) => (
            <div key={index} style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "10px" }}>
              {/* Plan Name */}
              <Form.Control
                type="text"
                placeholder="Enter Plan Name"
                value={pricePlan.plan_name}
                onChange={(e) => handlePriceChange(index, "plan_name", e.target.value)}
              />

              {/* Price */}
              <Form.Control
                type="number"
                placeholder="Enter Price"
                value={pricePlan.price}
                onChange={(e) => handlePriceChange(index, "price", e.target.value)}
              />

              {/* Duration */}
              <Form.Control
                type="text"
                placeholder="Enter Duration"
                value={pricePlan.plan_duration}
                onChange={(e) => handlePriceChange(index, "plan_duration", e.target.value)}
              />

              {/* Remove Button */}
              {prices.length > 1 && (
                <AiFillCloseCircle
                  size={25}
                  color="red"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleRemovePrice(index)}
                />
              )}
            </div>
          ))}

          {/* Add New Plan Button */}
          <Button variant="secondary" size="sm" onClick={handleAddPrice}>
            + Add Price Plan
          </Button>
        </Form.Group>

        {/* Images */}
        <Form.Group controlId="imagesInput">
          <Form.Label>Images</Form.Label>
          <Form.Control type="file" multiple accept="image/*" onChange={handleImageChange} />
        </Form.Group>
        <div className="preview-container">
          {images.map((image, index) => (
            <div key={index} className="preview-item">
              <AiFillCloseCircle className="remove-icon" onClick={() => removeImage(index)} />
              <img src={URL.createObjectURL(image)} alt="preview" className="preview-image" />
            </div>
          ))}
        </div>

       {/* Videos */}
       <Form.Group controlId="videosInput">
        <Form.Label>Videos</Form.Label>
        <Form.Control type="file" multiple accept="video/*" onChange={handleVideoChange} />
      </Form.Group>
      <div className="preview-container">
        {videos.map((video, index) => (
          <div key={index} className="preview-item">
            <AiFillCloseCircle className="remove-icon" onClick={() => removeVideo(index)} />
            <video src={video.url} controls className="preview-video" />
          </div>
        ))}
      </div>
        

        <Button className="mb-2 mt-3" variant="primary" onClick={handleAddProduct}>
          {loading ? "Adding..." : "Add Product"}
        </Button>
      </Form>
    </Container>
  );
};

export default CarModelManager;
